import React, { Component, Fragment } from "react"
import { Helmet } from "react-helmet"
import PropTypes from "prop-types"
import styled, { ThemeProvider } from "styled-components"
import { translate } from "react-i18next"
import { connect } from "react-redux"
import { compose } from "redux"
import stickybits from "stickybits"
import DashboardLoadingSpinner from "./DashboardLoadingSpinner"
import DashboardContents from "./DashboardContents"
import EmptyDashboardMessage from "./EmptyDashboardMessage"
import BrokenDashboardMessage from "./BrokenDashboardMessage"
import ReportFilters from "./filters/ReportFilters"
import { anyWidgetsRendered } from "../utils/object-utils"
import * as filterActions from "../state/actions/filterActions"

const RePinFiltersButton = styled.button`
  background-color: #fff;
  border: solid 1px #ccc;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  width: 30px;
  height: 50px;
  line-height: 50px;

  &:hover {
    cursor: pointer;
    background-color: #f0f0f0;
  }

  &:focus {
    outline: 0;
  }
`

class Dashboard extends Component {
  static propTypes = {
    children: PropTypes.node,
    errorMessageKey: PropTypes.string,
    isLoading: PropTypes.bool.isRequired,
    loadingFailed: PropTypes.bool.isRequired,
    onReload: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    widgetData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    theme: PropTypes.shape({
      primaryColor: PropTypes.string.isRequired,
      primaryColorHover: PropTypes.string.isRequired,
      navBackgroundColor: PropTypes.string.isRequired
    }).isRequired
  }

  static defaultProps = {
    children: null,
    errorMessageKey: null
  }

  componentDidMount() {
    this.stickyFilters = stickybits(".side-filters-container")
    window.addEventListener("resize", this.updateStickyFilterScroll)
  }

  componentWillUnmount = () => {
    window.removeEventListener("resize", this.updateStickyFilterScroll, false)
    this.stickyFilters.cleanup()
  }

  onToggleFullScreenFilters = isOpen => {
    this.props.toggleFullScreenFilters(isOpen) // eslint-disable-line react/prop-types
  }

  updateStickyFilterScroll = () => {
    this.stickyFilters.update()
  }

  toggleFullScreenFilters = () => {
    // eslint-disable-next-line react/prop-types
    this.onToggleFullScreenFilters(!this.props.filterState.showFiltersFullScreen)
  }

  render() {
    const {
      title,
      filterState,
      isLoading,
      children,
      widgetData,
      errorMessageKey,
      onReload,
      loadingFailed,
      theme,
      toggleFiltersPinned,
      t
    } = this.props

    // Set a variable passed down to WidgetContainer.jsx
    // which can be used for determining whether or not
    // to update/re-render the dashboard contents.
    // We only want to update if there's going to be a UI change
    // to the dashboard contents
    const contentsData = {
      loading: isLoading,
      data: null,
      errorMessageKey
    }

    // debugger; // prettier-ignore

    let contents
    if (isLoading) {
      // Show the loading message
      contents = <DashboardLoadingSpinner />
    } else if (loadingFailed) {
      contents = <BrokenDashboardMessage onReload={onReload} />
    } else if (!anyWidgetsRendered(children)) {
      // Loaded but no data, show the empty state warning
      contents = <EmptyDashboardMessage />
    } else {
      // Loaded and has data. Show each widget that has data
      contents = children
      contentsData.data = widgetData
    }

    return (
      <ThemeProvider theme={theme}>
        <Fragment>
          <Helmet>
            <title>{title}</title>
          </Helmet>
          {filterState.showFiltersFullScreen && (
            <div className="mobile-filters-container">
              <ReportFilters />
            </div>
          )}
          {!filterState.showFiltersFullScreen && (
            <div className="container-fluid px-0 h-100">
              <div className="row vh-100 no-gutters d-flex h-100">
                {filterState.filtersPinned && (
                  <div className="side-filters-container col-4 p-0 vh-100 h-100 w-sidebar d-none d-lg-flex sidebar d-print-none">
                    <div className="h-100 w-sidebar">
                      <ReportFilters />
                    </div>
                  </div>
                )}
                {!filterState.filtersPinned && (
                  <RePinFiltersButton
                    className="d-print-none"
                    title={t("Filters.SidebarToggleButton.Show")}
                    onClick={() => toggleFiltersPinned(true)}
                  >
                    <i className="mdi mdi-filter" />
                  </RePinFiltersButton>
                )}

                <div className="col px-3">
                  <DashboardContents
                    onReload={onReload}
                    errorMessageKey={contentsData.errorMessageKey}
                    title={title}
                    toggleFullScreenFilters={this.toggleFullScreenFilters}
                  >
                    {contents}
                  </DashboardContents>
                </div>
              </div>
            </div>
          )}
        </Fragment>
      </ThemeProvider>
    )
  }
}

function mapStateToProps(state) {
  return {
    filterState: state.filters,
    theme: state.userDetail.theme
  }
}

function mapDispatchToProps(dispatch) {
  return {
    toggleFullScreenFilters: isOpen => dispatch(filterActions.toggleFullScreenFilters(isOpen)),
    toggleFiltersPinned: isPinned => dispatch(filterActions.toggleFiltersPinned(isPinned))
  }
}

export default compose(
  translate(),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(Dashboard)



// WEBPACK FOOTER //
// ./src/components/Dashboard.jsx